import { useCallback, useRef, useState } from "react";
import { Loader2, UploadCloud } from "lucide-react";

/** Same upload-or-drop pattern as create-course.jsx's MainImageUpload,
 * generalized to any block's image field via the onUploadImage prop. */
export default function ImageUploadField({ value, onChange, onUploadImage }) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleFile = useCallback(
    async (file) => {
      if (
        !file ||
        (!file.type.startsWith("image/") && !/\.avif$/i.test(file.name))
      ) {
        setError("Please choose an image file.");
        return;
      }
      setError(null);
      setIsUploading(true);
      try {
        const url = await onUploadImage(file);
        onChange(url);
      } catch (err) {
        console.error("Image upload failed:", err);
        setError(err?.message || "Upload failed. Try again.");
      } finally {
        setIsUploading(false);
      }
    },
    [onUploadImage, onChange],
  );

  if (value && !isUploading) {
    return (
      <div className="relative group rounded-xl overflow-hidden border border-gray-200 h-40">
        <img
          src={value}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="px-4 py-2 rounded-lg bg-white text-[#0B2421] text-sm font-semibold"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={() => onChange("")}
            className="px-4 py-2 rounded-lg bg-white/90 text-red-600 text-sm font-semibold"
          >
            Remove
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.avif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </div>
    );
  }

  return (
    <div>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center justify-center h-40 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
          isDragging
            ? "border-[#0B2421] bg-[#0B2421]/5"
            : error
              ? "border-red-300 bg-red-50"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        {isUploading ? (
          <>
            <Loader2 className="h-6 w-6 text-[#0B2421] animate-spin mb-2" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </>
        ) : (
          <>
            <UploadCloud className="h-6 w-6 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-600">
              Click to upload or drag a photo here
            </span>
            <span className="text-xs text-gray-400 mt-1">
              PNG, JPG, or AVIF
            </span>
          </>
        )}
        <input
          type="file"
          accept="image/*,.avif"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
      </label>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
